import React, { createContext, useContext, useMemo, useState } from "react";
import { CircleTaskSchema } from "../models/schema/CircleTaskSchema";

type CircleTaskDictionary = Record<string, CircleTaskSchema>;

interface CircleTaskContextType {
  tasksMap: CircleTaskDictionary;
  getAllTasks: () => CircleTaskSchema[];
  getTaskById: (taskId: string) => CircleTaskSchema;
  setTask: (task: CircleTaskSchema) => void; // create or replace
  updateTask: (taskId: string, updated_task: CircleTaskSchema) => void;
  deleteTask: (taskId: string) => void;
  clearAll: () => void;
}

const CircleTaskContext = createContext<CircleTaskContextType | undefined>(
  undefined,
);

export const CircleTaskProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tasksMap, setTasksMap] = useState<CircleTaskDictionary>({});

  const getAllTasks = useMemo(() => {
    return () => Object.values(tasksMap);
  }, [tasksMap]);

  const getTaskById = (taskId: string) => tasksMap[taskId];

  const setTask = (task: CircleTaskSchema) => {
    setTasksMap((prev) => ({ ...prev, [task.id]: task }));
  };

  const updateTask = (taskId: string, updated_task: CircleTaskSchema) => {
    setTasksMap((prev) => {
      const existing = prev[taskId];
      if (!existing) return prev;
      const merged: CircleTaskSchema = {
        ...existing,
        ...updated_task,
        updated_at: updated_task.updated_at ?? new Date(),
      };
      return { ...prev, [taskId]: merged };
    });
  };

  const deleteTask = (taskId: string) => {
    setTasksMap((prev) => {
      const { [taskId]: _, ...rest } = prev;
      return rest;
    });
  };

  const clearAll = () => setTasksMap({});

  const value: CircleTaskContextType = {
    tasksMap,
    getAllTasks,
    getTaskById,
    setTask,
    updateTask,
    deleteTask,
    clearAll,
  };

  return (
    <CircleTaskContext.Provider value={value}>
      {children}
    </CircleTaskContext.Provider>
  );
};

export const useCircleTasks = (): CircleTaskContextType => {
  const ctx = useContext(CircleTaskContext);
  if (!ctx)
    throw new Error("useCircleTasks must be used within a CircleTaskProvider");
  return ctx;
};
